import Loader from '@/components/Loader';
import LoaderEcho from '@/components/LoaderEcho';
import { ApiError } from '@/services/queryUtils';
import GenericError from '@/components/states/GenericError';
import ServerError from '@/components/states/ServerError';

type ErrorTypes = Error | ApiError | null;

type CardContentVariant = 'decibel' | 'echo';

type Content<TData> = {
  isPending: boolean;
  isError: boolean;
  error: ErrorTypes;
  data: TData | undefined;
  renderData: (data: TData) => React.ReactNode;
  /**
   * When true (default), pending replaces the whole body with the variant loader (unmounts children).
   * Set false when the child manages its own loading UI (e.g. a chart slot) so React Query's
   * real `isPending` can be passed without faking it as false.
   */
  suspendOnPending?: boolean;
  /**
   * Variant for Echo vs Decibel within the card body - Defaults to `'decibel'`.
   */
  variant?: CardContentVariant;
};

const CardContent = <TData,>({
  isPending,
  isError,
  error,
  data,
  renderData,
  suspendOnPending = true,
  variant = 'decibel',
}: Content<TData>) => {
  const isApiError = (err: ErrorTypes): err is ApiError => {
    return err !== null && 'response' in err;
  };

  const isServerError = (err: ApiError): err is ApiError => {
    return err !== null && err.response?.status !== undefined && err.response?.status >= 500;
  };

  if (isPending && suspendOnPending) {
    return variant === 'echo' ? <LoaderEcho /> : <Loader />;
  }

  if (isError) {
    if (isApiError(error)) {
      if (isServerError(error)) {
        return <ServerError />;
      }
    }
    return <GenericError message={error?.message || 'An error occurred'} />;
  }

  return renderData(data ?? ({} as TData));
};

export default CardContent;
export type { CardContentVariant };
